import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useState, lazy, Suspense } from "react";
import PreLaunchGuard from "./components/PreLaunchGuard";
import ScreenshotProtection from "./components/ScreenshotProtection";
import ScrollToTop from "./components/ScrollToTop";

// Eager load critical routes
import ComingSoon from "./pages/ComingSoon";
import Index from "./pages/Index";

// Lazy load non-critical routes
const Beta = lazy(() => import("./pages/Beta"));
const Matches = lazy(() => import("./pages/Matches"));
const Admin = lazy(() => import("./pages/Admin"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Events = lazy(() => import("./pages/Events"));
const Careers = lazy(() => import("./pages/Careers"));
const SuccessStories = lazy(() => import("./pages/SuccessStories"));
const Safety = lazy(() => import("./pages/Safety"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Terms = lazy(() => import("./pages/Terms"));
const Behavior = lazy(() => import("./pages/Behavior"));
const FAQ = lazy(() => import("./pages/FAQ"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const App = () => {
  const [queryClient] = useState(() => new QueryClient());

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <ScreenshotProtection />
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <PreLaunchGuard>
            <Routes>
              <Route path="/" element={<ComingSoon />} />
              <Route path="/home" element={<Index />} />
              <Route path="/beta" element={<Suspense fallback={<PageLoader />}><Beta /></Suspense>} />
              <Route path="/matches" element={<Suspense fallback={<PageLoader />}><Matches /></Suspense>} />
              <Route path="/blog" element={<Suspense fallback={<PageLoader />}><Blog /></Suspense>} />
              <Route path="/blog/:slug" element={<Suspense fallback={<PageLoader />}><BlogPost /></Suspense>} />
              <Route path="/events" element={<Suspense fallback={<PageLoader />}><Events /></Suspense>} />
              <Route path="/careers" element={<Suspense fallback={<PageLoader />}><Careers /></Suspense>} />
              <Route path="/success-stories" element={<Suspense fallback={<PageLoader />}><SuccessStories /></Suspense>} />
              <Route path="/safety" element={<Suspense fallback={<PageLoader />}><Safety /></Suspense>} />
              <Route path="/privacy" element={<Suspense fallback={<PageLoader />}><PrivacyPolicy /></Suspense>} />
              <Route path="/terms" element={<Suspense fallback={<PageLoader />}><Terms /></Suspense>} />
              <Route path="/behavior" element={<Suspense fallback={<PageLoader />}><Behavior /></Suspense>} />
              <Route path="/faq" element={<Suspense fallback={<PageLoader />}><FAQ /></Suspense>} />
              <Route path="/admin" element={<Suspense fallback={<PageLoader />}><Admin /></Suspense>} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<Suspense fallback={<PageLoader />}><NotFound /></Suspense>} />
            </Routes>
          </PreLaunchGuard>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
